import { PayloadAction } from '@reduxjs/toolkit';
import { put, takeEvery } from 'redux-saga/effects';
import jsonFormData from 'json-form-data';
import { commonSlice, eventAddSlice, eventShowSlice, eventSingleShowSlice } from 'reduxToolkit/reducers';
import { eventAddReducerTypes, eventShowReducerTypes, eventSingleShowReducerTypes } from 'types';
import { apiCall, apiRoutes, getApiErrorMessage } from 'utils/helpers';
import { EVENT_ADD_SAGA, EVENT_SHOW_SAGA, EVENT_SINGLE_SHOW_SAGA } from 'reduxToolkit/actions/event/eventAction';

const { eventAddFailed, eventAddStart, eventAddSuccess } = eventAddSlice.actions;
const { eventShowFailed, eventShowStart, eventShowSuccess } = eventShowSlice.actions;
const { eventSingleShowFailed, eventSingleShowStart, eventSingleShowSuccess } = eventSingleShowSlice.actions;
const { exceptionHandlerRequest } = commonSlice.actions;

const eventAddSaga = function* (payloadData: PayloadAction<eventAddReducerTypes.IEventAddForm>): any {
    yield put(eventAddStart(payloadData.payload));
    try {
        const {
            id,
            title,
            short_description,
            description,
            start_date,
            end_date,
            start_time,
            end_time,
            duration,
            fees,
            offer,
            who_can_attend,
            type,
            uploadFile,
        } = payloadData.payload;

        const payload = {
            title,
            short_description,
            description,
            start_date,
            end_date,
            start_time,
            end_time,
            duration,
            fees,
            offer,
            who_can_attend,
            type,
            uploadFile,
        };

        const options = {
            initialFormData: new FormData(),
            showLeafArrayIndexes: true,
            includeNullValues: true,
            mapping: function (value: string | number) {
                if (typeof value === 'boolean') {
                    return +value ? '1' : '0';
                }
                return value;
            },
        };

        let url: any = null;
        const data = jsonFormData(payload, options);
        if (id) {
            url = { ...apiRoutes.updateEvent, data };
            url.apiPath = url.apiPath.replace(
                ':eventId',
                payloadData.payload.id ? payloadData.payload.id.toString() : ''
            );
        } else {
            url = { ...apiRoutes.addEvent, data };
        }

        const response = yield apiCall(url);

        if (response.status === 200) {
            yield put(eventAddSuccess(response.data));
        } else {
            const errorData = {
                status: false,
                error: getApiErrorMessage(response),
            };
            yield put(eventAddFailed(errorData));
        }
    } catch (error) {
        yield put(exceptionHandlerRequest(error));
    }
};

const eventShowSaga = function* (payloadData: PayloadAction<eventShowReducerTypes.IEventShowForm>): any {
    yield put(eventShowStart(payloadData.payload));
    try {
        const { is_front, is_upcoming, page, search_word, start_date, end_date } = payloadData.payload;

        const params: eventShowReducerTypes.IEventShowForm = {
            page,
            is_front,
        };

        if (is_upcoming) {
            params.is_upcoming = is_upcoming;
        }

        if (search_word) {
            params.search_word = search_word;
        }

        if (start_date && end_date) {
            params.start_date = start_date;
            params.end_date = end_date;
        }

        const url = { ...apiRoutes.showEvent, params };

        const response = yield apiCall(url);

        if (response.status === 200) {
            yield put(eventShowSuccess(response.data.data));
        } else {
            const errorData = {
                status: false,
                error: getApiErrorMessage(response),
            };
            yield put(eventShowFailed(errorData));
        }
    } catch (error) {
        yield put(exceptionHandlerRequest(error));
    }
};

const eventSingleShowSaga = function* (
    payloadData: PayloadAction<eventSingleShowReducerTypes.IEventSingleShowForm>
): any {
    yield put(eventSingleShowStart(payloadData.payload));
    try {
        let url: any = null;
        if (payloadData.payload.type === 'view') {
            url = { ...apiRoutes.showSingleEvent };
        } else {
            url = { ...apiRoutes.deleteEvent };
        }

        url.apiPath = url.apiPath.replace(
            ':eventId',
            payloadData.payload.eventId ? payloadData.payload.eventId.toString() : ''
        );

        const response = yield apiCall(url);

        if (response.status === 200) {
            yield put(eventSingleShowSuccess(response.data));
        } else {
            const errorData = {
                status: false,
                error: getApiErrorMessage(response),
            };
            yield put(eventSingleShowFailed(errorData));
        }
    } catch (error) {
        yield put(exceptionHandlerRequest(error));
    }
};

export const takeEventAddRequest = function* () {
    yield takeEvery(EVENT_ADD_SAGA, eventAddSaga);
};

export const takeEventShowRequest = function* () {
    yield takeEvery(EVENT_SHOW_SAGA, eventShowSaga);
};

export const takeEventSingleShowRequest = function* () {
    yield takeEvery(EVENT_SINGLE_SHOW_SAGA, eventSingleShowSaga);
};
